/**
 * DaysBars — wholesale and retail timing on one shared day scale.
 *
 * Days-to-sell and days supply answer different questions (how fast one unit
 * moves vs how long existing inventory lasts), and the wholesale and retail
 * figures are only meaningful next to each other, so they share an axis with
 * this listing's own age.
 */
import React from 'react';
import './DaysBars.css';

export type DaysBarTone = 'self' | 'wholesale' | 'retail';

export interface DaysBar {
  key: string;
  label: string;
  value?: number | null;
  hint?: string;
  tone?: DaysBarTone;
}

interface DaysBarsProps {
  bars: DaysBar[];
  emptyText?: string;
}

const usable = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0;

export const DaysBars: React.FC<DaysBarsProps> = ({
  bars,
  emptyText = 'No timing data for this market.',
}) => {
  const present = bars.filter((b) => usable(b.value));
  if (present.length === 0) return <div className="db-empty">{emptyText}</div>;

  const max = Math.max(...present.map((b) => b.value as number));

  return (
    <div className="db">
      {present.map((b) => (
        <div className="db-row" key={b.key}>
          <span className={`db-label${b.hint ? ' db-label--help' : ''}`} title={b.hint}>
            {b.label}
          </span>
          <div className="db-track">
            <div
              className={`db-fill db-fill--${b.tone ?? 'wholesale'}`}
              style={{ width: max > 0 ? `${((b.value as number) / max) * 100}%` : '0%' }}
            />
          </div>
          <span className="db-value">{Math.round(b.value as number).toLocaleString()}d</span>
        </div>
      ))}
    </div>
  );
};

export default DaysBars;
